import React, { useState, useEffect, useRef } from 'react';
import { useApp } from '../../context/AppContext';
import { useTranslations } from '../../context/TranslationsContext';
import { whatsappAPI } from '../../api';

const ChatView = ({ conversation, onSendMessage }) => {
  const { state } = useApp();
  const { translations } = useTranslations();
  const t = translations[state.currentLanguage];

  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [lastSentMessage, setLastSentMessage] = useState('');
  const [lastSentTime, setLastSentTime] = useState(0);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (conversation) {
      fetchMessages();
    }
  }, [conversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchMessages = async () => {
    if (!conversation || !state.token) return;
    
    setLoading(true);
    try {
      const messagesData = await whatsappAPI.getConversations(state.token, null, conversation.phoneNumber);
      setMessages(messagesData);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !conversation || sending) return;

    const messageToSend = newMessage;
    const now = Date.now();
    
    // Prevent duplicate messages within 2 seconds
    if (lastSentMessage === messageToSend && (now - lastSentTime) < 2000) {
      console.log('Duplicate message prevented:', messageToSend);
      return;
    }
    
    console.log('ChatView: Starting to send message:', { messageToSend, phoneNumber: conversation.phoneNumber });
    setSending(true);
    setLastSentMessage(messageToSend);
    setLastSentTime(now);
    
    // Add message to UI immediately for better UX
    const newMsg = {
      _id: Date.now().toString(),
      message_content: messageToSend,
      message_type: 'bot',
      timestamp: new Date().toISOString(),
      phone_number: conversation.phoneNumber,
      session_id: conversation.id
    };
    
    setMessages(prev => [...prev, newMsg]);
    setNewMessage('');
    
    try {
      if (onSendMessage) {
        await onSendMessage(conversation.phoneNumber, messageToSend);
      } else {
        // Fallback if no callback provided
        await whatsappAPI.sendMessage(state.token, conversation.phoneNumber, messageToSend);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      alert('خطأ في إرسال الرسالة');
      // Remove the message from UI if sending failed
      setMessages(prev => prev.filter(msg => msg._id !== newMsg._id));
    } finally {
      setSending(false);
    }
  };

  const formatMessageTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('ar-SA', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatMessageDate = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) {
      return 'اليوم';
    } else if (diffInDays === 1) {
      return 'أمس';
    } else {
      return date.toLocaleDateString('ar-SA', { 
        day: 'numeric', 
        month: 'long' 
      });
    }
  };

  const groupMessagesByDate = (messages) => {
    const groups = {};
    messages.forEach(message => {
      const date = new Date(message.timestamp).toDateString();
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(message);
    });
    return groups;
  };

  if (!conversation) {
    return (
      <div className="chat-view">
        <div className="chat-view-empty">
          <div className="empty-chat-icon">💬</div>
          <h3>اختر محادثة</h3>
          <p>اختر محادثة من القائمة لعرض الرسائل</p>
        </div>
      </div>
    );
  }

  const messageGroups = groupMessagesByDate(messages);

  return (
    <div className="chat-view">
      <div className="chat-header">
        <div className="chat-header-info">
          <div className="chat-avatar">
            <div className="avatar-circle">
              {conversation.phoneNumber.slice(-2)}
            </div>
            {conversation.isActive && <div className="online-indicator"></div>}
          </div>
          <div className="chat-details">
            <h3>
              {conversation.isCustomer ? (
                <div className="customer-chat-info">
                  <div className="customer-name">{conversation.customerName}</div>
                  <div className="phone-number">{conversation.phoneNumber}</div>
                </div>
              ) : (
                conversation.phoneNumber
              )}
            </h3>
            <p className="chat-status">
              {conversation.isActive ? 'نشط الآن' : 'غير متصل'}
              {conversation.isCustomer && (
                <span className="customer-badge">عميل مسجل</span>
              )}
            </p>
          </div>
        </div>
        <div className="chat-actions">
          <button className="chat-action-btn" title="معلومات المحادثة">
            <span>ℹ️</span>
          </button>
        </div>
      </div>

      <div className="chat-messages">
        {loading ? (
          <div className="chat-loading">جاري تحميل الرسائل...</div>
        ) : Object.keys(messageGroups).length === 0 ? (
          <div className="no-messages">
            <div className="no-messages-icon">📝</div>
            <p>لا توجد رسائل في هذه المحادثة</p>
          </div>
        ) : (
          Object.entries(messageGroups).map(([date, dateMessages]) => (
            <div key={date} className="message-date-group">
              <div className="date-divider">
                <span>{formatMessageDate(dateMessages[0].timestamp)}</span>
              </div>
              {dateMessages.map((message) => (
                <div
                  key={message._id}
                  className={`message ${message.message_type === 'user' ? 'sent' : 'received'}`}
                >
                  <div className="message-content">
                    <div className="message-text">
                      {message.message_content}
                    </div>
                    <div className="message-time">
                      {formatMessageTime(message.timestamp)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input">
        <form onSubmit={handleSendMessage} className="message-form">
          <div className="input-container">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="اكتب رسالة..."
              className="message-input"
              disabled={sending}
            />
            <button
              type="submit"
              className="send-button"
              disabled={!newMessage.trim() || sending}
            >
              {sending ? '⏳' : '📤'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChatView;
